/**
 * Auto-populates site detail form fields from a matched test location row.
 *
 * @module siteDetailsAutoPopulateHelper
 */
import {
  TestLocationAddressEntry,
  TestLocationDetail,
} from "../../../api/services/siteNpiDetails/siteNpiDetailsTypes";
import { getTestLocationFieldValue } from "./siteDetailsFieldOptionsHelper";
import {
  blankIfMissingSiteDetailsPlaceholder,
  SiteDetailsFormValues,
} from "./siteDetailsForm.types";

/** Address form fields filled from the first `address_details` entry. */
const ADDRESS_FORM_FIELDS = ["address", "city", "state", "zip"] as const;

/** Fields filled from the `address_details` entry that matches the selected street. */
const ADDRESS_SIBLING_FIELDS = ["city", "state", "zip"] as const;

/** One address form field populated from `address_details`. */
type AddressFormField = (typeof ADDRESS_FORM_FIELDS)[number];

/** Form fields that trigger lookup and auto-populate of sibling fields. */
export const SITE_DETAILS_AUTO_POPULATE_TRIGGER_FIELDS = [
  "site_name",
  "npi",
] as const;

/** Subset of form keys that may trigger auto-populate. */
export type SiteDetailsAutoPopulateTriggerField =
  (typeof SITE_DETAILS_AUTO_POPULATE_TRIGGER_FIELDS)[number];

/**
 * Returns whether a form field key triggers auto-populate on change.
 *
 * @param fieldKey - Site details form field key.
 * @returns True when changing this field should attempt a location lookup.
 */
export const isSiteDetailsAutoPopulateTriggerField = (
  fieldKey: keyof SiteDetailsFormValues,
): fieldKey is SiteDetailsAutoPopulateTriggerField =>
  SITE_DETAILS_AUTO_POPULATE_TRIGGER_FIELDS.includes(
    fieldKey as SiteDetailsAutoPopulateTriggerField,
  );

/**
 * Finds the first cached test location row matching a trigger field value.
 *
 * @param fieldKey - Trigger field that was changed (`site_name` or `npi`).
 * @param value - New value for the trigger field.
 * @param locations - Cached test location rows from Redux.
 * @returns Matching row when found; otherwise undefined.
 */
export const findTestLocationByFieldValue = (
  fieldKey: SiteDetailsAutoPopulateTriggerField,
  value: string,
  locations: TestLocationDetail[],
): TestLocationDetail | undefined => {
  const normalizedValue = value.trim();
  if (!normalizedValue) {
    return undefined;
  }

  return locations.find(
    (location) =>
      getTestLocationFieldValue(location, fieldKey) === normalizedValue,
  );
};

/**
 * Reads one address field from the first `address_details` entry.
 *
 * @param location - Matched test location row.
 * @param fieldKey - Address, city, state, or zip.
 * @returns Trimmed value from the first entry, or an empty string when missing.
 */
const readFirstAddressDetailField = (
  location: TestLocationDetail,
  fieldKey: AddressFormField,
): string => {
  const rawValue = location.address_details?.[0]?.[fieldKey];
  return typeof rawValue === "string"
    ? blankIfMissingSiteDetailsPlaceholder(rawValue)
    : "";
};

/**
 * Reads one address field from the first `address_details` entry.
 *
 * @param location - Matched test location row.
 * @param fieldKey - Address, city, state, or zip.
 * @returns Trimmed value from the first entry, or an empty string when missing.
 */
export const getFirstAddressDetailFieldValue = (
  location: TestLocationDetail,
  fieldKey: AddressFormField,
): string => readFirstAddressDetailField(location, fieldKey);

/**
 * Finds the first cached location whose alias list contains `alias`.
 *
 * @param alias - Alias selected in the form.
 * @param locations - Cached test location rows from Redux.
 * @returns Matching row when found; otherwise undefined.
 */
export const findTestLocationByAlias = (
  alias: string,
  locations: TestLocationDetail[],
): TestLocationDetail | undefined => {
  const normalizedAlias = alias.trim();
  if (!normalizedAlias) {
    return undefined;
  }

  return locations.find((location) =>
    location.alias?.some((entry) => entry?.trim() === normalizedAlias),
  );
};

/**
 * Replaces the other site fields from the location that owns `alias`.
 *
 * NPI, Name, and address fields are overwritten only when the matched
 * location has a value for that field. Address values come from the first
 * `address_details` entry. When no location matches, only `alias` changes.
 *
 * @param currentValues - Form state before the alias change.
 * @param alias - Newly selected alias.
 * @param locations - Cached test location rows from Redux.
 * @returns Form values reflecting the selected alias.
 */
export const applySelectedAliasToFormValues = (
  currentValues: SiteDetailsFormValues,
  alias: string,
  locations: TestLocationDetail[],
): SiteDetailsFormValues => {
  const nextValues: SiteDetailsFormValues = {
    ...currentValues,
    alias,
  };
  const matchedLocation = findTestLocationByAlias(alias, locations);

  if (!matchedLocation) {
    return nextValues;
  }

  const siteName = getTestLocationFieldValue(matchedLocation, "site_name");
  if (siteName) {
    nextValues.site_name = siteName;
  }

  const npi = getTestLocationFieldValue(matchedLocation, "npi");
  if (npi) {
    nextValues.npi = npi;
  }

  ADDRESS_FORM_FIELDS.forEach((fieldKey) => {
    const matchedValue =
      readFirstAddressDetailField(matchedLocation, fieldKey) ||
      getTestLocationFieldValue(matchedLocation, fieldKey);
    if (matchedValue) {
      nextValues[fieldKey] = matchedValue;
    }
  });

  return nextValues;
};

/**
 * Finds the `address_details` entry for the selected NPI and street.
 *
 * Searches every cached location that shares `npi` and returns the first
 * entry whose address matches.
 *
 * @param npi - NPI currently selected in the form.
 * @param address - Street address the user selected.
 * @param locations - Cached test location rows from Redux.
 * @returns Matching address entry when found; otherwise undefined.
 */
export const findAddressDetailForSelectedNpi = (
  npi: string,
  address: string,
  locations: TestLocationDetail[],
): TestLocationAddressEntry | undefined => {
  const selectedNpi = npi.trim();
  const selectedAddress = address.trim();
  if (!selectedNpi || !selectedAddress) {
    return undefined;
  }

  for (const location of locations) {
    if ((location.npi ?? "").trim() !== selectedNpi) {
      continue;
    }

    const matchedEntry = location.address_details?.find(
      (entry) => entry?.address?.trim() === selectedAddress,
    );
    if (matchedEntry) {
      return matchedEntry;
    }
  }

  return undefined;
};

/**
 * Replaces city, state, and zip from the selected NPI's matching address.
 *
 * The street is set to `address`. City, state, and zip are overwritten from
 * the matching `address_details` entry. A missing value stays `""` so it is
 * not saved as a placeholder. Alias, NPI, and Name stay unchanged. When no
 * entry matches, only the street changes.
 *
 * @param currentValues - Form state before the address change.
 * @param address - Newly selected street address.
 * @param locations - Cached test location rows from Redux.
 * @returns Form values with city, state, and zip for that address.
 */
export const applySelectedAddressToFormValues = (
  currentValues: SiteDetailsFormValues,
  address: string,
  locations: TestLocationDetail[],
): SiteDetailsFormValues => {
  const nextValues: SiteDetailsFormValues = {
    ...currentValues,
    address: blankIfMissingSiteDetailsPlaceholder(address),
  };
  const matchedEntry = findAddressDetailForSelectedNpi(
    currentValues.npi,
    address,
    locations,
  );

  if (!matchedEntry) {
    return nextValues;
  }

  ADDRESS_SIBLING_FIELDS.forEach((fieldKey) => {
    const rawValue = matchedEntry[fieldKey];
    nextValues[fieldKey] =
      typeof rawValue === "string"
        ? blankIfMissingSiteDetailsPlaceholder(rawValue)
        : "";
  });

  return nextValues;
};

/**
 * Replaces name and address fields from the location that owns `npi`.
 *
 * Name becomes that location's site name, or `""` when the location has
 * none, so the Name-required check still blocks save. Address, city, state,
 * and zip become the first `address_details` entry, with `""` for any missing
 * value. Alias is left unchanged. When no location matches, only `npi` is updated.
 *
 * @param currentValues - Form state before the NPI change.
 * @param npi - Newly selected NPI.
 * @param locations - Cached test location rows from Redux.
 * @returns Form values reflecting the selected NPI.
 */
export const applySelectedNpiToFormValues = (
  currentValues: SiteDetailsFormValues,
  npi: string,
  locations: TestLocationDetail[],
): SiteDetailsFormValues => {
  const nextValues: SiteDetailsFormValues = {
    ...currentValues,
    npi,
  };
  const matchedLocation = findTestLocationByFieldValue("npi", npi, locations);

  if (!matchedLocation) {
    return nextValues;
  }

  nextValues.site_name = getTestLocationFieldValue(
    matchedLocation,
    "site_name",
  );
  ADDRESS_FORM_FIELDS.forEach((fieldKey) => {
    nextValues[fieldKey] = getFirstAddressDetailFieldValue(
      matchedLocation,
      fieldKey,
    );
  });

  return nextValues;
};

/**
 * Replaces NPI and address fields from the location matching the selected name.
 *
 * Name receives the selected value. Alias and Type stay unchanged.
 * Address fields come from the first `address_details` entry, or `""` when missing.
 *
 * @param currentValues - Form state before the trigger field change.
 * @param matchedLocation - Test location row matched by the trigger field.
 * @param name - Selected site name.
 * @returns Updated form values with NPI and address fields replaced from the match.
 */
export const applyMatchedNameToFormValues = (
  currentValues: SiteDetailsFormValues,
  matchedLocation: TestLocationDetail,
  name: string,
): SiteDetailsFormValues => {
  const nextValues: SiteDetailsFormValues = {
    ...currentValues,
    site_name: name,
    npi: getTestLocationFieldValue(matchedLocation, "npi"),
  };

  ADDRESS_FORM_FIELDS.forEach((fieldKey) => {
    nextValues[fieldKey] = getFirstAddressDetailFieldValue(
      matchedLocation,
      fieldKey,
    );
  });

  return nextValues;
};

/**
 * Applies trigger-field auto-populate when `alias`, `address`, `site_name`,
 * or `npi` changes.
 *
 * An NPI change replaces Name with the matched location's site name and
 * replaces Address, City, State, and Zip with the first `address_details`
 * entry (`""` when a value is missing). An alias change overwrites NPI,
 * Name, and those address fields when the matched location has a value.
 * An address change overwrites City, State, and Zip from the matching
 * `address_details` entry of the selected NPI. A name change replaces NPI
 * and address fields from the matched location, using empty strings for missing values.
 *
 * @param currentValues - Form state before the change.
 * @param changedField - Field the user edited.
 * @param changedValue - New value for the changed field.
 * @param locations - Cached test location rows from Redux.
 * @returns Updated form values, with sibling fields filled when a match exists.
 */
export const applySiteDetailsAutoPopulate = (
  currentValues: SiteDetailsFormValues,
  changedField: keyof SiteDetailsFormValues,
  changedValue: string,
  locations: TestLocationDetail[],
): SiteDetailsFormValues => {
  if (changedField === "npi") {
    return applySelectedNpiToFormValues(currentValues, changedValue, locations);
  }

  if (changedField === "alias") {
    return applySelectedAliasToFormValues(
      currentValues,
      changedValue,
      locations,
    );
  }

  if (changedField === "address") {
    return applySelectedAddressToFormValues(
      currentValues,
      changedValue,
      locations,
    );
  }

  const nextValues: SiteDetailsFormValues = {
    ...currentValues,
    [changedField]: changedValue,
  };

  if (!isSiteDetailsAutoPopulateTriggerField(changedField)) {
    return nextValues;
  }

  const matchedLocation = findTestLocationByFieldValue(
    changedField,
    changedValue,
    locations,
  );

  if (!matchedLocation) {
    return nextValues;
  }

  return applyMatchedNameToFormValues(
    currentValues,
    matchedLocation,
    changedValue,
  );
};
